import React, { useMemo, useCallback } from "react"

import { ComponentProps } from "@/stitches/types"
import { CSS } from "@stitches/react"

import { Copy, ICopyProps } from "@/shared/components"

import { SLink } from "./Link.styles"

interface ILinkProps extends Omit<ComponentProps<typeof SLink>, "onClick"> {
  as?: "a" | "button"
  scale?: ICopyProps["scale"]
  fontWeight?: ICopyProps["fontWeight"]
  noWrap?: boolean
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  spanStyles?: CSS
  shouldPreventDefault?: boolean
  clearLinkDefaultStyles?: boolean
}

export const Link = ({
  children,
  as = "a",
  shouldPreventDefault = false,
  onClick,
  scale = 8,
  fontWeight,
  noWrap,
  clearLinkDefaultStyles = false,
  spanStyles,
  ...props
}: ILinkProps): React.ReactElement => {
  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => {
      if (shouldPreventDefault) {
        event.preventDefault()
      }

      onClick && onClick(event)
    },
    [shouldPreventDefault, onClick],
  )

  const linkProps = useMemo(() => {
    const { className, target, ...rest } = props
    const commonLinkProps = {
      ...rest,
      as,
      target,
      className,
    }

    if (as === "a" && clearLinkDefaultStyles) {
      return {
        ...commonLinkProps,
        style: { textDecoration: "none", color: "inherit" },
      }
    }

    if (target === "_blank") {
      return {
        ...commonLinkProps,
        rel: "noopener noreferrer",
      }
    }

    return commonLinkProps
  }, [props, as, clearLinkDefaultStyles])

  return (
    <SLink data-ui="link" onClick={handleOnClick} {...linkProps}>
      {!noWrap ? (
        <Copy as="span" scale={scale} fontWeight={fontWeight} css={spanStyles}>
          {children}
        </Copy>
      ) : (
        children
      )}
    </SLink>
  )
}
